<template>
  <div
    ref="dropdownWrapper"
    class="dropdown d-inline-block"
    v-on="$listeners"
  >
    <m-dropdown-trigger
      ref="dropdownActivator"
      :trigger-label="triggerLabel"
      :trigger-variant="triggerVariant"
      :trigger-icon-variant="triggerIconVariant"
      :trigger-theme="triggerTheme"
      :trigger-size="triggerSize"
      :trigger-disabled="triggerDisabled"
      @dropdown-trigger="updateShowDropdown"
    >
      <slot name="dropdown-trigger" />
    </m-dropdown-trigger>
    <transition
      v-for="(menuNumber, menuIndex) in getMenuDepth"
      :key="`menu-${menuNumber}`"
      name="fade"
    >
      <m-dropdown-menu
        v-show="showDropdown[menuIndex]"
        ref="popper"
        :width="width"
        :height-limit="heightLimit"
        :class="showDropdown[menuIndex] ? 'show' : null"
        @on-bottom="$emit('on-bottom', menuNumber)"
      >
        <m-dropdown-search
          v-if="getEnabledSearch(menuIndex)"
          ref="dropdownSearchContainer"
          :input-value.sync="inputSearchValue[menuIndex]"
          :placeholder="placeholder"
          :state="state"
          :input-id="menuIndex"
          :debounce="debounce"
          @input="updateFilteredContent"
          @search-blur="updateShowDropdown(false)"
        >
          <slot name="dropdown-search" />
        </m-dropdown-search>
        <m-dropdown-item-container
          @on-bottom="$emit('on-bottom', menuNumber)"
        >
          <slot
            v-if="enableMultiLevel"
            :content="filteredContent(menuIndex)"
            :active-item-id="activeItemId"
            :is-children-active="showDropdown[menuNumber]"
            :set-item-action="setItemAction"
            :menu-number="menuNumber"
            :name="`menu-${menuNumber}`"
          />
          <slot
            v-else
            :menu-number="menuNumber"
            :set-item-action="setItemAction"
            :content="filteredContent(menuIndex)"
          >
            <m-dropdown-item class="text-center text-muted">
              {{ emptyDropdownText }}
            </m-dropdown-item>
          </slot>
        </m-dropdown-item-container>
      </m-dropdown-menu>
    </transition>
  </div>
</template>

<script>
import MDropdownTrigger from './components/DropdownTrigger';
import MDropdownMenu from './components/DropdownMenu';
import MDropdownSearch from './components/DropdownSearch';
import MDropdownItem from './components/DropdownItem';
import MDropdownItemContainer from './components/DropdownItemContainer';
import { createPopper } from '@popperjs/core';

export default {
  name: 'MDropdown',
  components: {
    MDropdownTrigger,
    MDropdownMenu,
    MDropdownSearch,
    MDropdownItem,
    MDropdownItemContainer,
  },
  props: {
    triggerLabel: {
      type: String,
      default: 'Button',
    },
    triggerVariant: {
      type: String,
      default: 'icon-right',
    },
    triggerIconVariant: {
      type: String,
      default: 'dropdown',
    },
    triggerTheme: {
      type: String,
      default: 'primary',
    },
    triggerSize: {
      type: String,
      default: 'default',
    },
    triggerDisabled: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'bottom-start',
    },
    childrenPlacement: {
      type: String,
      default: 'right-start',
    },
    content: {
      type: Array,
      default: () => [],
    },
    enableSearch: {
      type: [Boolean, Array],
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Search',
    },
    heightLimit: {
      type: String,
      default: 'fixed',
    },
    width: {
      type: String,
      default: 'max-content',
    },
    state: {
      type: Boolean,
      default: null,
    },
    emptyDropdownText: {
      type: String,
      default: 'No data found',
    },
    enableMultiLevel: {
      type: Boolean,
      default: false,
    },
    toggleShowDropdown: {
      type: Boolean,
      default: false,
    },
    dropdownDistance: {
      type: Number,
      default: 4,
    },
    debounce: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    showDropdown: [],
    selectedDropdown: {},
    dropdownSkidding: [],
    popperInstances: [],
    contentModel: [],
    inputSearchValue: [],
    inputActiveId: 0,
    activeItemId: null,
  }),
  watch: {
    toggleShowDropdown(value) {
      this.updateShowDropdown(value);
    },
    content: {
      deep: true,
      handler(value) {
        this.contentModel[0] = this.content;
        this.contentModel = this.contentModel.map((el, index) => {
          if (index > 0) {
            return el.map(insideEl => {
              const element = this.$dropdownPlugin.findContentFromElement(value, insideEl);
              if (element) {
                return element;
              }
              return insideEl;
            });
          }
          return el;
        });
      },
    },
    showDropdown: {
      deep: true,
      handler(value) {
        if (!value[0]) {
          this.resetInputSearchValue();
        }
        this.$nextTick(() => {
          this.resetPopperInstance();
        });
      },
    },
    inputSearchValue: {
      deep: true,
      handler() {
        this.filteredContent(this.inputActiveId);
        this.$emit('dropdown-menu-search', this.inputSearchValue);
      },
    },
  },
  computed: {
    getMenuDepth() {
      return this.$dropdownPlugin.getContentDepth(this.content);
    },
  },
  beforeMount() {
    this.initiateData();
  },
  mounted() {
    window.addEventListener('mousedown', this.dropdownEventListener);
  },
  beforeDestroy() {
    window.removeEventListener('mousedown', this.dropdownEventListener);
  },
  methods: {
    getEnabledSearch(menuNumber) {
      if (this.enableSearch.length) {
        const index = menuNumber >= this.enableSearch.length ? this.enableSearch.length - 1 : menuNumber;
        return this.enableSearch[index];
      }
      return this.enableSearch;
    },
    filteredContent(menuNumber = 0) {
      const selectedContent = this.contentModel.length ? this.contentModel[menuNumber] : this.content;
      return selectedContent.filter(element => {
        const elContent = element.content.toLowerCase();
        return elContent.indexOf(this.inputSearchValue[menuNumber].toLowerCase()) > -1;
      });
    },
    resetInputSearchValue() {
      this.inputSearchValue = this.inputSearchValue.map(() => '');
    },
    initiateData() {
      for (let i = 0; i < this.getMenuDepth; i += 1) {
        this.showDropdown.push(false);
        this.inputSearchValue.push('');
        this.dropdownSkidding.push(0);
        if (i === 0) {
          this.contentModel.push(this.content);
        } else {
          this.contentModel.push([]);
        }
      }
    },
    dropdownEventListener(e) {
      const self = this; // eslint-disable-line consistent-this
      const refsDropdownWrapper = self.$refs.dropdownWrapper;
      const isDropdownAbsent = typeof refsDropdownWrapper === 'undefined';
      const isOutsideDropdown = !isDropdownAbsent && e.target.closest('.dropdown') !== refsDropdownWrapper;
      if (isOutsideDropdown) {
        self.updateShowDropdown(false);
        self.$emit('blur-dropdown');
      }
    },
    updateFilteredContent(event) {
      const { value, inputId } = event;
      this.updateShowDropdown(false, inputId + 1);
      this.inputSearchValue = this.inputSearchValue.map((el, index) => index === inputId ? value : el);
      this.inputActiveId = inputId;
    },
    updateChildrenItems(dropdownItem, menuNumber, offsetTop) {
      if (dropdownItem.children && dropdownItem.children.length) {
        const itemHeight = 36;
        const searchHeight = 52;
        this.activeItemId = dropdownItem.id;
        this.dropdownSkidding[menuNumber] = Math.round(offsetTop / itemHeight) * itemHeight;
        if (this.enableSearch.length && this.enableSearch[menuNumber - 1]) {
          this.dropdownSkidding[menuNumber] += searchHeight;
        }
        this.contentModel[menuNumber] = dropdownItem.children;
        this.updateShowDropdown(true, menuNumber);
      } else {
        this.updateShowDropdown(false, menuNumber);
      }
    },
    setItemAction(dropdownItem, menuNumber, event, options = {}) {
      const rectItem = event.target ? event.target.getBoundingClientRect() : 0;
      const rectContainer = this.$refs.popper[menuNumber - 1].$el.getBoundingClientRect();
      const {
        isSelectingItem,
        isHiddenAfterAction,
      } = options;
      if (isSelectingItem) {
        this.selectedDropdown = dropdownItem;
        this.$emit('selected-dropdown', this.selectedDropdown);
      }
      if (isHiddenAfterAction) {
        this.updateShowDropdown(false);
      }
      this.$emit('dropdown-menu-number-active', menuNumber);
      this.$emit('dropdown-item-active', dropdownItem);
      this.updateChildrenItems(dropdownItem, menuNumber, rectItem.top - rectContainer.top);
    },
    setShowDropdown(val, menuNumber) {
      this.showDropdown[menuNumber] = val;
    },
    sanitizeShowDropdown(menuNumber) {
      let dropdownMenuNumber = menuNumber + 1;
      while (dropdownMenuNumber < this.getMenuDepth) {
        this.showDropdown[dropdownMenuNumber] = false;
        dropdownMenuNumber += 1;
      }
      this.showDropdown = this.showDropdown.map((el, index) => {
        if (this.showDropdown[index - 1] === false) {
          return false;
        }
        return el;
      });
    },
    updateShowDropdown(val, menuNumber = 0) {
      if (menuNumber === 0) {
        this.showDropdown = this.showDropdown.map(() => false);
      }
      this.setShowDropdown(val, menuNumber);
      this.sanitizeShowDropdown(menuNumber);
    },
    resetPopperInstance() {
      const popperElements = [this.$refs.dropdownActivator];
      let number = 0;
      while (number < this.getMenuDepth) {
        popperElements.push(this.$refs.popper[number]);
        number += 1;
      }
      let arrIndex = 0;
      let dropdownPlacement = this.placement;
      while (arrIndex < this.getMenuDepth) {
        if (this.popperInstances[arrIndex]) {
          this.popperInstances[arrIndex].destroy();
          this.popperInstances[arrIndex] = null;
        }
        if (arrIndex > 0) {
          dropdownPlacement = this.childrenPlacement;
        }
        this.popperInstances[arrIndex] = createPopper(popperElements[arrIndex].$el, popperElements[arrIndex + 1].$el, {
          placement: dropdownPlacement,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [this.dropdownSkidding[arrIndex], this.dropdownDistance],
              },
            },
          ],
        });
        arrIndex += 1;
      }
    },
  },
};
</script>
<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
